import React from "react";
import { DataCell } from "@/components/DataCell";
import { PercentChange } from "@/components/DataCell/PercentChange";
import { useTranslation } from "react-i18next";
import { useContextMetrics } from "@/hooks/useContextMetrics";
import { OrganizationMetricsContextEnum } from "@/__generated__/types";

interface BulkMessageDataCellsProps {
  orgId?: string;
  orgIdLoading?: boolean;
  orgIdError?: unknown;
}

export const BulkMessageDataCells: React.FC<BulkMessageDataCellsProps> = ({
  orgId,
  orgIdLoading = false,
  orgIdError,
}) => {
  const { t } = useTranslation();

  const {
    loading: metricsLoading,
    error: metricsError,
    getPreviousCycleValue,
    getMetricDisplayValue,
    getMetricCalculatedPercentChanged,
  } = useContextMetrics(orgId, OrganizationMetricsContextEnum.BulkMessages);

  const isLoading = orgIdLoading;
  const hasError = orgIdError;
  const hasRequiredData = Boolean(orgId);

  const showLoading = () => {
    if (hasError || metricsError) return false;
    return isLoading || metricsLoading;
  };

  const showError = () => {
    return hasError || metricsError || (!hasRequiredData && !isLoading);
  };

  const errorContent = (
    <span className="text-sm text-muted-foreground">
      {t("bulkMessaging.bentos.error.loadingError")}
    </span>
  );

  return (
    <div className="flex flex-col gap-4">
      <DataCell
        className="min-h-[137px]"
        isLoading={showLoading()}
        title={t("bulkMessaging.bentos.totalCampaigns")}
        tooltipText={t("bulkMessaging.bentos.totalCampaignsToolTip")}
        middleValue={
          showError() ? errorContent : getMetricDisplayValue("totalCampaigns")
        }
        bottomValue={
          showError() ? null : (
            <PercentChange
              value={getMetricCalculatedPercentChanged("totalCampaigns")}
              prevCount={getPreviousCycleValue("totalCampaigns")}
              customTemplate={t("metrics.comparisons.yearOverYear", {
                percentChanged: "{{percentChange}}",
              })}
            />
          )
        }
      />

      <DataCell
        className="min-h-[137px]"
        isLoading={showLoading()}
        title={t("bulkMessaging.bentos.totalMessages")}
        tooltipText={t("bulkMessaging.bentos.totalMessagesToolTip")}
        middleValue={
          showError() ? errorContent : getMetricDisplayValue("totalMessageSent")
        }
        bottomValue={
          showError() ? null : (
            <PercentChange
              value={getMetricCalculatedPercentChanged("totalMessageSent")}
              prevCount={getPreviousCycleValue("totalMessageSent")}
              customTemplate={t("metrics.comparisons.yearOverYear", {
                percentChanged: "{{percentChange}}",
              })}
            />
          )
        }
      />
    </div>
  );
};
